import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Grid,
  Snackbar,
  Alert,
  Stack,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/auth.service.js';
import { LeadForm } from '../crm/LeadForm.js';
import { useLeads } from '../../hooks/useLeads.js';
import { HistoricoVisitas } from '../../modules/visitas/components/HistoricoVisitas.js';
import type { LeadFormInput } from '@azoupvendas/shared';

// AIDEV-NOTE: Shape de cliente retornado por GET /clientes/:codigo — espelha sync_clientes
// (ver ClienteFullRow em packages/server/src/routes/clientes.routes.ts).
interface ClienteDetalhe {
  id: number;
  client_id: number;
  codigo: number;
  cpf_cnpj: string | null;
  nome_razao_social: string | null;
  nome_fantasia: string | null;
  cidade: string | null;
  estado: string | null;
  cep: string | null;
  endereco: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  fone: string | null;
  celular: string | null;
  email: string | null;
  contato: string | null;
  inscricao_estadual: string | null;
  inativo: string | null;
  data_ultima_alteracao: string | null;
}

// AIDEV-NOTE: Pedido retornado por GET /pedidos?cliente_codigo=:codigo
interface PedidoRow {
  id: string;
  numero?: number | string | null;
  valor_total?: number | null;
  status?: string;
  created_at?: string;
}

interface PedidosApiResponse {
  pedidos: PedidoRow[];
  total?: number;
}

// AIDEV-NOTE: Campos editáveis enviados ao PUT /clientes/:codigo.
// CIDADE, RAZAO_SOCIAL, CPF_CNPJ, INSCRICAO_ESTADUAL, INATIVO são read-only no app.
interface CamposEditaveis {
  nome_fantasia: string;
  fone: string;
  celular: string;
  email: string;
  contato: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cep: string;
  estado: string;
}

const CAMPOS_VAZIOS: CamposEditaveis = {
  nome_fantasia: '',
  fone: '',
  celular: '',
  email: '',
  contato: '',
  endereco: '',
  numero: '',
  complemento: '',
  bairro: '',
  cep: '',
  estado: ''
};

function clienteToForm(c: ClienteDetalhe): CamposEditaveis {
  return {
    nome_fantasia: c.nome_fantasia ?? '',
    fone: c.fone ?? '',
    celular: c.celular ?? '',
    email: c.email ?? '',
    contato: c.contato ?? '',
    endereco: c.endereco ?? '',
    numero: c.numero ?? '',
    complemento: c.complemento ?? '',
    bairro: c.bairro ?? '',
    cep: c.cep ?? '',
    estado: c.estado ?? ''
  };
}

// AIDEV-NOTE: Mapeamento de status do pedido para cor do Chip MUI (mantido do componente anterior)
const STATUS_COLOR: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  rascunho: 'default',
  finalizado: 'warning',
  sincronizado: 'success',
  erro_sync: 'error'
};

const currencyBRL = (v: number | null | undefined) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

interface ClienteDetailDialogProps {
  clienteCodigo: number | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Diálogo de detalhes do cliente.
 *
 * AIDEV-NOTE: 3 abas — Dados (read/edit) | Pedidos (últimos pedidos do cliente) | Visitas.
 *   - Dados: carrega via GET /clientes/:codigo e edita via PUT /clientes/:codigo.
 *   - Pedidos: GET /pedidos?cliente_codigo=:codigo (substitui o antigo useClienteDetail).
 *   - Visitas: componente HistoricoVisitas existente.
 */
export function ClienteDetailDialog({ clienteCodigo, open, onClose }: ClienteDetailDialogProps) {
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [form, setForm] = useState<CamposEditaveis>(CAMPOS_VAZIOS);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: 'success' | 'error';
    message: string;
  }>({ open: false, severity: 'success', message: '' });

  const queryClient = useQueryClient();
  const { createLead } = useLeads();

  // AIDEV-NOTE: GET /clientes/:codigo — detalhe completo. Habilitado apenas com Dialog aberto e código válido.
  const clienteQuery = useQuery({
    queryKey: ['cliente-detalhe', clienteCodigo],
    enabled: open && clienteCodigo != null,
    queryFn: async () => {
      const res = await api.get<{ cliente: ClienteDetalhe }>(`/clientes/${clienteCodigo}`);
      return res.data.cliente;
    }
  });

  // AIDEV-NOTE: Últimos pedidos do cliente — usa GET /pedidos com filtro cliente_codigo.
  // Substitui o antigo hook useClienteDetail (que usava supabase direto, hoje deprecado).
  const pedidosQuery = useQuery({
    queryKey: ['cliente-pedidos', clienteCodigo],
    enabled: open && clienteCodigo != null,
    queryFn: async () => {
      const res = await api.get<PedidosApiResponse>('/pedidos', {
        params: { cliente_codigo: clienteCodigo, limit: 5 }
      });
      return res.data.pedidos ?? [];
    }
  });

  // AIDEV-NOTE: Sincroniza estado do form com os dados do cliente sempre que carregar
  // ou quando o modo volta para 'view' (reset implícito ao cancelar).
  useEffect(() => {
    if (clienteQuery.data) {
      setForm(clienteToForm(clienteQuery.data));
    }
  }, [clienteQuery.data]);

  // AIDEV-NOTE: Reset de estado quando o dialog abre/fecha — evita "lembrar" tab/modo entre clientes.
  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setMode('view');
    }
  }, [open]);

  const updateMutation = useMutation({
    mutationFn: async (campos: CamposEditaveis) => {
      await api.put(`/clientes/${clienteCodigo}`, campos);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clientes-lista'] });
      void queryClient.invalidateQueries({ queryKey: ['cliente-detalhe', clienteCodigo] });
      setMode('view');
      setSnackbar({ open: true, severity: 'success', message: 'Cliente atualizado com sucesso.' });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Erro ao atualizar cliente.';
      setSnackbar({ open: true, severity: 'error', message: msg });
    }
  });

  const handleFieldChange =
    (key: keyof CamposEditaveis) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const handleSave = () => {
    updateMutation.mutate(form);
  };

  const handleCancel = () => {
    if (clienteQuery.data) {
      setForm(clienteToForm(clienteQuery.data));
    }
    setMode('view');
  };

  const handleCreateLead = async (data: LeadFormInput) => {
    await createLead.mutateAsync(data);
    setLeadFormOpen(false);
  };

  const cliente = clienteQuery.data;
  const isAtivo = cliente?.inativo !== 'S';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Detalhes do Cliente</Typography>
            <IconButton onClick={onClose} aria-label="fechar">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Dados" />
            <Tab label="Pedidos" />
            <Tab label="Visitas" />
          </Tabs>

          {/* ── Aba 0 — Dados ──────────────────────────────────────────────── */}
          {activeTab === 0 && (
            <Box>
              {clienteQuery.isLoading && (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {clienteQuery.isError && (
                <Alert severity="error">Não foi possível carregar o cliente.</Alert>
              )}

              {cliente && (
                <>
                  {/* Cabeçalho com ações de edição */}
                  <Box display="flex" justifyContent="flex-end" mb={2}>
                    {mode === 'view' ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => setMode('edit')}
                      >
                        Editar
                      </Button>
                    ) : (
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          color="inherit"
                          startIcon={<CancelIcon />}
                          onClick={handleCancel}
                          disabled={updateMutation.isPending}
                        >
                          Cancelar
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<SaveIcon />}
                          onClick={handleSave}
                          disabled={updateMutation.isPending}
                        >
                          {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
                        </Button>
                      </Stack>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    {/* Razão Social — sempre read-only */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Razão Social"
                        value={cliente.nome_razao_social ?? ''}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    {/* Nome Fantasia — editável */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Nome Fantasia"
                        value={mode === 'edit' ? form.nome_fantasia : (cliente.nome_fantasia ?? '')}
                        onChange={handleFieldChange('nome_fantasia')}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: mode === 'view' }}
                      />
                    </Grid>

                    {/* CNPJ/CPF — sempre read-only */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="CNPJ / CPF"
                        value={cliente.cpf_cnpj ?? ''}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    {/* Inscrição Estadual — sempre read-only */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Inscrição Estadual"
                        value={cliente.inscricao_estadual ?? ''}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    {/* Situação */}
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          Situação:
                        </Typography>
                        <Chip
                          label={isAtivo ? 'Ativo' : 'Inativo'}
                          color={isAtivo ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* Endereço / Número — editáveis */}
                    <Grid item xs={12} sm={8}>
                      <TextField
                        label="Endereço"
                        value={mode === 'edit' ? form.endereco : (cliente.endereco ?? '')}
                        onChange={handleFieldChange('endereco')}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: mode === 'view' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Número"
                        value={mode === 'edit' ? form.numero : (cliente.numero ?? '')}
                        onChange={handleFieldChange('numero')}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: mode === 'view' }}
                      />
                    </Grid>

                    {/* Complemento / Bairro — editáveis */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Complemento"
                        value={mode === 'edit' ? form.complemento : (cliente.complemento ?? '')}
                        onChange={handleFieldChange('complemento')}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: mode === 'view' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Bairro"
                        value={mode === 'edit' ? form.bairro : (cliente.bairro ?? '')}
                        onChange={handleFieldChange('bairro')}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: mode === 'view' }}
                      />
                    </Grid>

                    {/* Cidade (read-only) / Estado (editável) */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Cidade"
                        value={cliente.cidade ?? ''}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                        helperText={mode === 'edit' ? 'Cidade não é editável pelo app' : undefined}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Estado"
                        value={mode === 'edit' ? form.estado : (cliente.estado ?? '')}
                        onChange={handleFieldChange('estado')}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: mode === 'view' }}
                        inputProps={{ maxLength: 2 }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="CEP"
                        value={mode === 'edit' ? form.cep : (cliente.cep ?? '')}
                        onChange={handleFieldChange('cep')}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: mode === 'view' }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* Telefone / Celular — editáveis */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Telefone"
                        value={mode === 'edit' ? form.fone : (cliente.fone ?? '')}
                        onChange={handleFieldChange('fone')}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: mode === 'view' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Celular"
                        value={mode === 'edit' ? form.celular : (cliente.celular ?? '')}
                        onChange={handleFieldChange('celular')}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: mode === 'view' }}
                      />
                    </Grid>

                    {/* E-mail / Contato — editáveis */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="E-mail"
                        type="email"
                        value={mode === 'edit' ? form.email : (cliente.email ?? '')}
                        onChange={handleFieldChange('email')}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: mode === 'view' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Contato"
                        value={mode === 'edit' ? form.contato : (cliente.contato ?? '')}
                        onChange={handleFieldChange('contato')}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: mode === 'view' }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          )}

          {/* ── Aba 1 — Pedidos ────────────────────────────────────────────── */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Últimos Pedidos
              </Typography>
              {pedidosQuery.isLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={20} />
                </Box>
              ) : (pedidosQuery.data ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum pedido registrado.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>N.</TableCell>
                      <TableCell>Valor</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(pedidosQuery.data ?? []).map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.numero ?? '—'}</TableCell>
                        <TableCell>{currencyBRL(p.valor_total)}</TableCell>
                        <TableCell>
                          <Chip
                            label={p.status ?? '—'}
                            size="small"
                            color={STATUS_COLOR[p.status ?? ''] ?? 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}

          {/* ── Aba 2 — Visitas ────────────────────────────────────────────── */}
          {activeTab === 2 && clienteCodigo != null && (
            <HistoricoVisitas clienteId={String(clienteCodigo)} />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setLeadFormOpen(true)}>
            Criar Lead
          </Button>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <LeadForm
        open={leadFormOpen}
        onClose={() => setLeadFormOpen(false)}
        onSubmit={handleCreateLead}
        defaultValues={{ estagio: 'novo' }}
        title="Criar Lead para este Cliente"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
